import { BadgeInfo, Check, ChevronDown, ScrollText, ShieldCheck, Sparkles } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { CASE_STUDIES, type CaseStudy } from "@/components/marketing/caseStudies";
import { usePricingCopy } from "@/lib/pricingContext";

const RADIUS = 36;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const STROKE = 7;

/**
 * Case-study showcase for /how-it-works. Sticky device-frame preview
 * on the left, click-to-expand accordion on the right; the active
 * case drives the preview content. Each preview is a realistic mock
 * of the actual session output (gauge + excerpt + citations) so
 * visitors *see* the artifact before they're asked to pay.
 *
 * Per docs/plans/10-HowItWorks-Page-Content.md §3, every case is
 * labeled "Illustrative" and uses generic patient identifiers + payer
 * categories. Outcome framing is published RANGES, never a stated
 * result for the illustrative patient.
 */
export function CaseStudyShowcase() {
  const [open, setOpen] = useState(0);
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const active = CASE_STUDIES[open] ?? CASE_STUDIES[0]!;

  return (
    <section ref={ref} className="bg-surface py-24" aria-labelledby="how-case-studies">
      <div className="wrap">
        <div className="grid grid-cols-1 items-start gap-16 lg:grid-cols-2">
          {/* Sticky preview — device-frame mock of the session output */}
          <div
            className={`transition-all duration-700 ease-out lg:sticky lg:top-24 ${
              visible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"
            }`}
          >
            <div className="mb-8">
              <span className="mb-3 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-primary">
                <Sparkles size={12} aria-hidden />
                What the output looks like
              </span>
              <h2
                id="how-case-studies"
                className="mb-4 font-heading text-3xl font-extrabold tracking-tight text-ink sm:text-4xl"
              >
                Three illustrative cases
              </h2>
              <p className="max-w-lg text-base text-muted">
                The probability score, the excerpt, and the cited sources are exactly what a real
                session produces — for cases shaped like these.
              </p>
            </div>

            <PreviewFrame study={active} />

            <p className="mt-4 text-[11px] italic leading-snug text-muted">
              Scenarios constructed to demonstrate the product. Not real patients or insurer
              decisions. Probability scores are model estimates for the scenario described;
              results vary case-by-case.
            </p>
          </div>

          {/* Accordion — one row per case study */}
          <div
            className={`flex flex-col gap-2 transition-all delay-100 duration-700 ease-out ${
              visible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"
            }`}
          >
            {CASE_STUDIES.map((study, i) => (
              <AccordionRow
                key={study.id}
                study={study}
                index={i}
                isOpen={open === i}
                onSelect={() => setOpen(i)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function AccordionRow({
  study,
  index,
  isOpen,
  onSelect,
}: {
  study: CaseStudy;
  index: number;
  isOpen: boolean;
  onSelect: () => void;
}) {
  const pct = Math.round(study.probability * 100);
  const isRefund = study.kind === "refund";

  return (
    <div
      className={`overflow-hidden rounded border bg-bg transition ${
        isOpen ? "border-primary/40 shadow-sm" : "border-border hover:border-primary/30"
      }`}
    >
      <button
        type="button"
        onClick={onSelect}
        aria-expanded={isOpen}
        className="flex w-full cursor-pointer items-center gap-3.5 px-5 py-5 text-left"
      >
        <span
          className={`w-7 flex-shrink-0 font-heading text-xs font-extrabold tracking-wider transition-colors ${
            isOpen ? "text-primary" : "text-muted"
          }`}
        >
          {String(index + 1).padStart(2, "0")}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2">
            <span
              className={`font-heading text-base font-bold tracking-tight transition-colors ${
                isOpen ? "text-primary" : "text-ink"
              }`}
            >
              {study.patientLabel}
            </span>
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted">
              {study.cptCode}
            </span>
          </div>
          <p className="mt-0.5 truncate text-xs text-muted">{study.procedure}</p>
        </div>
        <span
          className={`hidden flex-shrink-0 items-center rounded-full border px-2 py-0.5 text-[10px] font-bold tabular-nums sm:inline-flex ${
            isRefund
              ? "border-warning/30 bg-warning/10 text-warning"
              : "border-success/30 bg-success/10 text-success"
          }`}
        >
          {pct}%
        </span>
        <span
          className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border transition ${
            isOpen ? "border-primary bg-primary text-white" : "border-border text-muted"
          }`}
        >
          {isOpen ? (
            <Check size={12} strokeWidth={2.25} aria-hidden />
          ) : (
            <ChevronDown size={12} strokeWidth={2.25} aria-hidden />
          )}
        </span>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ease-bounce-soft ${
          isOpen ? "max-h-[260px]" : "max-h-0"
        }`}
      >
        <div className="space-y-3 pb-5 pl-[60px] pr-5">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted">
              Why denied
            </p>
            <p className="mt-1 text-sm leading-relaxed text-ink">{study.denialSummary}</p>
          </div>
          <div className="rounded-lg border-l-2 border-primary/40 bg-surface px-3 py-2">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted">
              Outcome (published range)
            </p>
            <p className="mt-1 text-xs leading-relaxed text-ink">{study.outcomeFraming}</p>
            <p className="mt-1.5 text-[10px] italic text-muted">
              Source: {study.outcomeSource}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function PreviewFrame({ study }: { study: CaseStudy }) {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-bg shadow-lg">
      {/* Window chrome — three-dot row above the preview frame */}
      <div className="flex items-center justify-between border-b border-border px-5 py-3">
        <div className="flex items-center gap-2">
          <ScrollText size={13} strokeWidth={1.75} aria-hidden className="text-primary" />
          <p className="font-heading text-[11px] font-bold uppercase tracking-wider text-muted">
            Session preview · {study.patientLabel}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 rounded-full border border-border bg-surface px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-muted">
            <BadgeInfo size={10} strokeWidth={2.25} aria-hidden />
            Illustrative
          </span>
          <div className="flex gap-1.5">
            <span aria-hidden className="h-2 w-2 rounded-full bg-danger/70" />
            <span aria-hidden className="h-2 w-2 rounded-full bg-warning/80" />
            <span aria-hidden className="h-2 w-2 rounded-full bg-success/80" />
          </div>
        </div>
      </div>

      <div className="animate-fade-in p-6">
        {/* Top row: gauge + denial summary */}
        <div className="flex items-start gap-5">
          <Gauge value={study.probability} kind={study.kind} />
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-primary">
              {study.cptCode} · {study.payerCategory}
            </p>
            <h3 className="mt-1 font-heading text-lg font-extrabold tracking-tight text-ink">
              {study.procedure}
            </h3>
            <p className="mt-2 text-xs leading-relaxed text-muted">{study.denialSummary}</p>
          </div>
        </div>

        {/* Body: drafted excerpt OR refund banner */}
        <div className="mt-6 border-t border-border pt-5">
          {study.kind === "appeal" ? <ExcerptBody study={study} /> : <RefundBody study={study} />}
        </div>
      </div>
    </div>
  );
}

function Gauge({ value, kind }: { value: number; kind: CaseStudy["kind"] }) {
  const pct = Math.round(Math.max(0, Math.min(1, value)) * 100);
  const offset = CIRCUMFERENCE - Math.max(0, Math.min(1, value)) * CIRCUMFERENCE;
  const tone = kind === "refund" ? "text-warning" : "text-ink";
  return (
    <div
      className="relative h-[88px] w-[88px] flex-shrink-0"
      role="img"
      aria-label={`Estimated probability of overturn: ${pct} percent`}
    >
      <svg viewBox="0 0 88 88" className="h-full w-full -rotate-90">
        <circle
          cx="44"
          cy="44"
          r={RADIUS}
          fill="none"
          stroke="rgb(var(--color-border))"
          strokeWidth={STROKE}
        />
        <circle
          cx="44"
          cy="44"
          r={RADIUS}
          fill="none"
          stroke="url(#case-showcase-gauge)"
          strokeWidth={STROKE}
          strokeLinecap="round"
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 600ms ease-out" }}
        />
        <defs>
          <linearGradient id="case-showcase-gauge" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgb(var(--color-primary))" />
            <stop offset="100%" stopColor="rgb(var(--color-accent))" />
          </linearGradient>
        </defs>
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`font-heading text-xl font-extrabold leading-none ${tone}`}>{pct}%</span>
        <span className="mt-0.5 text-[8px] font-semibold uppercase tracking-wider text-muted">
          overturn
        </span>
      </div>
    </div>
  );
}

function ExcerptBody({ study }: { study: CaseStudy }) {
  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-primary">
          <Sparkles size={10} strokeWidth={2.25} aria-hidden />
          Drafted excerpt
        </span>
        <span className="text-[10px] text-muted">
          Section: <span className="font-semibold text-ink">Medical Necessity</span>
        </span>
      </div>
      <p className="text-[13px] leading-relaxed text-ink/90">{study.excerpt}</p>
      {study.citations && study.citations.length > 0 && (
        <ul className="mt-4 space-y-1.5 border-t border-border pt-3">
          {study.citations.map((c) => (
            <li key={c.marker} className="flex items-start gap-2 text-[11px] text-muted">
              <span className="rounded border border-success/30 bg-success/10 px-1.5 py-0.5 font-bold text-success">
                {c.marker}
              </span>
              <span className="leading-snug">{c.label}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RefundBody({ study }: { study: CaseStudy }) {
  const { paidPriceLabel } = usePricingCopy();
  return (
    <div className="rounded-lg border border-warning/40 bg-warning/5 p-5">
      <div className="flex items-center gap-2">
        <ShieldCheck size={16} strokeWidth={2} className="text-warning" aria-hidden />
        <p className="font-heading text-sm font-bold uppercase tracking-wider text-warning">
          We&apos;d refund your fee
        </p>
      </div>
      <p className="mt-3 text-[13px] leading-relaxed text-ink">
        Based on what we found, an appeal is unlikely to succeed for {study.patientLabel}. Rather
        than charge for a draft we don&apos;t believe in, we&apos;d refund the{" "}
        <span className="font-semibold">{paidPriceLabel}</span> and walk you through what would need to
        change clinically before re-submitting.
      </p>
      <p className="mt-3 text-[11px] italic leading-snug text-muted">
        Most competitors would sell you the appeal anyway. Trust matters more.
      </p>
    </div>
  );
}
